import React, { FormEvent, useEffect, useRef, useState, FC, useMemo } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import { iconSize } from '../../constants/icon.constants';
import { QueryParamsConst } from '../../constants/query-params.constants';
import { updateSearchResults } from '../../hooks/useQueryParams.effect';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { documentService } from '../../utils/window.utils';
import Icon from '../@shared/icon';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import Filter from './components/filter';
import Pagination from '../@shared/pagination';
import Suggestions from './components/suggestions';
import { SearchApiModel } from './models/searchApiModels';
import { TSearchItem, TSearchProps } from './types/search.types';

const SearchResults: FC<WithTranslation & TSearchProps> = (props) => {
  const { sitecoreContext, t } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const searchQueryString = location?.search;
  const searchParams = useMemo(() => new URLSearchParams(location?.search), [searchQueryString]);
  const page = searchParams.get(QueryParamsConst.PAGE) || '1';
  const query = searchParams.get(QueryParamsConst.QUERY) || '';
  const perPage = searchParams.get(QueryParamsConst.PER_PAGE) || '10';
  const filters = searchParams.get(QueryParamsConst.FILTERS) || '';
  const [inputValue, setInputValue] = useState(query);
  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx.md})`);
  const [, setSuggestionQuery] = useState('');
  const [isSuggestionsComponentVisible, setIsSuggestionsComponentVisible] = useState(false);
  const SUGGESTIONS_MIN_CHAR_LIMIT = 3;
  const SEPARATOR_CHAR = ',';
  const model = new SearchApiModel(props);
  const formRef = useRef<HTMLFormElement>(null);
  const pages = Math.ceil(model.counter / parseInt(perPage, 10));
  const activeFilters = filters.length ? filters.split(SEPARATOR_CHAR) : [];
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  /**
   * searchResultsRef is created to check and retain the results view,
   * if the results view is displayed atleast once in the page
   * navigating to other pages refer bug: 415822
   */
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const onSubmit: (e: FormEvent<HTMLFormElement>) => void = (e: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!inputValue) return;
    updateSearchResults(
      navigate,
      {
        [QueryParamsConst.QUERY]: inputValue || '',
        [QueryParamsConst.FILTERS]: '',
        [QueryParamsConst.PAGE]: '1',
        [QueryParamsConst.PER_PAGE]: '10',
      },
      model.contextItemUrl
    );
  };

  const onPaginationChange = (selectedPage: number): void => {
    updateSearchResults(navigate, { [QueryParamsConst.PAGE]: selectedPage.toString() }, model.contextItemUrl);
  };
  const onFiltersChange = (filterValue: string[]): void => {
    updateSearchResults(
      navigate,
      {
        [QueryParamsConst.FILTERS]: filterValue.join(SEPARATOR_CHAR),
        [QueryParamsConst.PAGE]: '1',
      },
      model.contextItemUrl
    );
  };
  const updateSuggestionsQuery = (event): void => {
    setSuggestionQuery(event?.target?.value);
  };
  const onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void = (e) => {
    if (e?.key === 'Enter') {
      formRef.current?.dispatchEvent(new Event('submit', {bubbles: true}));
      return;
    }

    updateSuggestionsQuery(e);
  };

  const onUpdateQueryFromSuggestions = (q: string): void => {
    if (!inputValue) {
      return;
    }
    setInputValue(q);
    setIsSuggestionsComponentVisible(false);
    updateSearchResults(
      navigate,
      {
        [QueryParamsConst.QUERY]: q,
        [QueryParamsConst.FILTERS]: '',
        [QueryParamsConst.PAGE]: '1',
      },
      model.contextItemUrl
    );
  };

  const handleClickOutside = (event): void => {
    if (formRef.current?.contains(event?.target) || !isSuggestionsComponentVisible) {
      return;
    }

    setIsSuggestionsComponentVisible(false);
  };

  useEffect(() => {
    documentService().addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService().removeEventListener('mouseup', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const toggleCollapsed = (): void => {
    setIsCollapsed(!isCollapsed);
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  const arrowIcon = isCollapsed ? 'arrow-chevron-down' : 'arrow-chevron-up';
  const searchFilters = isMobile ? (
    <div className="search-results__results__accordian">
      <button
        onClick={(): void => {
          toggleCollapsed();
        }}
      >
        <h3 className="search-results__results__accordian--title">{t('cnhi-filters')}</h3>
        <Icon size={iconSize.dsk} iconName={arrowIcon} />
      </button>
      {!isCollapsed && <Filter filters={model?.filters} activeFilters={activeFilters} onFiltersChange={onFiltersChange} />}
    </div>
  ) : (
    <Filter filters={model?.filters} activeFilters={activeFilters} onFiltersChange={onFiltersChange} />
  );

  const searchResultsList = model?.results?.length ? (
    <div className="search-results__results__list">
      {model?.results?.map((result: TSearchItem) => (
        <article className={'search-results__item'}>
          <header>
            <RoutableNotEditableLink href={result?.link} target="_blank">
              <h3 className="search-results__item__title">{result?.title}</h3>
              <RichText
                field={{ value: result?.description }}
                editable={false}
                tag="div"
                className="search-results__item__description rich-text"
              />
              <RichText field={{ value: result?.highlight }} editable={false} tag="div" className="search-results__item__snippet" />
            </RoutableNotEditableLink>
          </header>
        </article>
      ))}
    </div>
  ) : (
    <div className="search-results__no-results">
      <Icon iconName="info-alert-notif-info" size={iconSize.xlg} extraClassName="search-results__no-results--icon" />
      <h3 className="search-results__no-results__title">{t('cnhi-noMatchesFoundTitle')}</h3>
      <p className="search-results__no-results__description">{t('cnhi-noMatchesFoundDescription')}</p>
    </div>
  );

  return (
    <div className="search-results">
      <div className="search-results__bar">
        <h2 className="search-results__title">{t('cnhi-searchTitle')}</h2>
        <div className="search-results__subtitle">
          {query && (
            <>
              <p className="search-results__subtitle-results">
                {t('cnhi-searchTerm')}: {`"${query}"`}
              </p>
              <p className="search-results__subtitle-results">
                {t('cnhi-totalResults')}: {model?.counter}
              </p>
            </>
          )}
        </div>
        <form onSubmit={onSubmit} ref={formRef}>
          <div className="search-results__wrapper">
            <div className="search-results__input-row">
              <div className="search-results__input-control">
                <Icon iconName="action-search" size={iconSize.dsk} extraClassName="search-results__input--search-icon" />
                {isSuggestionsComponentVisible && (
                  <Suggestions
                    className="search-suggestions--search-page-input"
                    language={sitecoreContext?.language || ''}
                    contextId={sitecoreContext?.itemId || ''}
                    suggestionQuery={inputValue || ''}
                    minimalLength={SUGGESTIONS_MIN_CHAR_LIMIT}
                    onUpdateQuery={onUpdateQueryFromSuggestions}
                  />
                )}
                <div className="search-results__input-control-container">
                  {inputValue && <label className="search-results__input-control-container-label">{t('cnhi-searchInputLabel')}</label>}
                  <input
                    name="Search"
                    placeholder={t('cnhi-searchInputPlaceholder')}
                    type="text"
                    className="search-results__input"
                    value={inputValue}
                    onKeyUp={onKeyUp}
                    onChange={(e): void => setInputValue(e?.target?.value)}
                    onFocus={(): void => setIsSuggestionsComponentVisible(true)}
                    autoComplete="off"
                  />
                </div>
              </div>
              <button type="submit" className={'search-results__button--primary'} disabled={!inputValue}>
                {t('cnhi-searchCTA')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {(query || searchResultsRef?.current) ? (
        <div className="search-results__results" ref={searchResultsRef}>
          {searchFilters}
          {searchResultsList}
        </div>
      ) : (
        <div className="search-results__content">
          <Icon iconName="action-search" size={iconSize.xlg} extraClassName="search-results__no-results--icon" />
          <h3 className="search-results__no-results__title">{t('cnhi-initialSearchTitle')}</h3>
          <p className="search-results__no-results__description">{t('cnhi-initialSearchDescription')}</p>
        </div>
      )}

      <Pagination currentPage={parseInt(page, 10)} pages={pages} goToPage={onPaginationChange} />
    </div>
  );
};

export default withTranslation()(withSitecoreContext()(SearchResults));
